import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import formbricks from "@formbricks/js/app";
import * as Sentry from "@sentry/react";

import config from "config";

import { userSelectors } from "store/user/userSelectors";

type FormbricksApi = {
  init: (options: {
    environmentId: string;
    apiHost: string;
    userId: string;
  }) => Promise<unknown>;
  registerRouteChange: () => Promise<unknown>;
};

const formbricksApi = formbricks as unknown as FormbricksApi;

// The @formbricks/js loader fetches the SDK source over HTTP and injects it as an inline
// <script>, which our strict CSP (script-src without 'unsafe-inline') blocks. Loading the
// SDK through a src-based <script> tag first makes the loader skip that path: it reuses
// window.formbricks whenever it is already defined.
const loadFormbricksSdkScript = (apiHost: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if ("formbricks" in window) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `${apiHost}/api/packages/app`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load the Formbricks SDK script"));
    document.head.appendChild(script);
  });

const FormbricksProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const userId = useSelector(userSelectors.userId);
  const location = useLocation();
  const hasInitialized = useRef(false); // guards calling formbricksApi.init multiple times in case of re-rendering
  const [isInitialized, setIsInitialized] = useState(false); // to trigger the re-render after formbricksApi.init has been called

  useEffect(() => {
    if (
      !config.formbricks.environmentId ||
      !config.formbricks.apiHost ||
      !userId ||
      hasInitialized.current
    ) {
      return;
    }

    hasInitialized.current = true;
    void loadFormbricksSdkScript(config.formbricks.apiHost)
      .then(() =>
        formbricksApi.init({
          environmentId: config.formbricks.environmentId,
          apiHost: config.formbricks.apiHost,
          userId,
        })
      )
      .then(() => setIsInitialized(true))
      .catch((error: unknown) => {
        hasInitialized.current = false;
        Sentry.captureException(error);
      });
  }, [userId, location.pathname]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    void formbricksApi.registerRouteChange();
  }, [location.pathname, isInitialized]);

  return <>{children}</>;
};

export default FormbricksProvider;
